import { Component } from '@theme/component';

/**
 * @typedef {Object} RowScrollerRefs
 * @property {HTMLElement} list - The row that scrolls sideways
 * @property {HTMLElement} [controls] - Wrapper for the arrows and indicator, hidden when nothing overflows
 * @property {HTMLButtonElement} [previous] - Button that scrolls back
 * @property {HTMLButtonElement} [next] - Button that scrolls forward
 * @property {HTMLElement[]} [dots] - One marker per item, used when the row holds a few items
 * @property {HTMLElement} [thumb] - Progress marker, used when the row holds many items
 */

/**
 * Shows arrows and a position indicator under a sideways-scrolling row so shoppers can tell it swipes.
 * Works in both text directions.
 *
 * @extends {Component<RowScrollerRefs>}
 */
class RowScrollerComponent extends Component {
  requiredRefs = ['list'];

  /** @type {AbortController | undefined} */
  #abort;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    this.refs.list.addEventListener('scroll', this.#update, { passive: true, signal: this.#abort.signal });
    this.#resizeObserver = new ResizeObserver(this.#update);
    this.#resizeObserver.observe(this.refs.list);
    this.#update();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#resizeObserver?.disconnect();
  }

  get #range() {
    const { list } = this.refs;
    return list.scrollWidth - list.clientWidth;
  }

  get #progress() {
    return this.#range > 0 ? Math.min(1, Math.abs(this.refs.list.scrollLeft) / this.#range) : 0;
  }

  #update = () => {
    const { list, controls, previous, next, dots, thumb } = this.refs;
    const progress = this.#progress;

    if (controls) controls.hidden = this.#range < 8;
    if (previous) previous.disabled = progress < 0.02;
    if (next) next.disabled = progress > 0.98;

    if (dots?.length) {
      const active = Math.round(progress * (dots.length - 1));
      dots.forEach((dot, index) => dot.toggleAttribute('data-active', index === active));
    }

    if (thumb) {
      const size = Math.max(0.15, list.clientWidth / list.scrollWidth);
      thumb.style.inlineSize = `${size * 100}%`;
      thumb.style.insetInlineStart = `${progress * (1 - size) * 100}%`;
    }
  };

  /**
   * @param {boolean} forward
   */
  #move(forward) {
    const { list, dots } = this.refs;
    const step = dots?.length ? this.#range / Math.max(1, dots.length - 1) : list.clientWidth * 0.7;
    const direction = getComputedStyle(list).direction === 'rtl' ? -1 : 1;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    list.scrollBy({ left: step * (forward ? 1 : -1) * direction, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  /** Scrolls one step back. */
  previous() {
    this.#move(false);
  }

  /** Scrolls one step forward. */
  next() {
    this.#move(true);
  }
}

if (!customElements.get('row-scroller')) {
  customElements.define('row-scroller', RowScrollerComponent);
}
